import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Box, Typography } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { AnimatePresence, motion } from "framer-motion";
import { colors } from "../../theme/colors";
import OverviewEloProgressionChart from "./OverviewEloProgressionChart";
import OverviewRatingTrendChart from "./OverviewRatingTrendChart";
import MapPerformanceList from "./MapPerformanceList";
import MapPlayRadarChart from "./MapPlayRadarChart";
import MapCombatTrendChart from "./MapCombatTrendChart";
import {
    buildEloProgressionStats,
    buildPerformanceCombatStats,
    buildProfileMatches,
    buildRating3ProgressionStats,
    buildRating3TrendSeries,
    getProfileTotalMatchCount,
    shouldUseMatchSummaryForRecord,
} from "./profileRecentMatches";
import { EmptyDataState } from "../common/EmptyDataState";
import {
    ShowChartRounded,
    MapRounded,
    HubRounded,
    TimelineRounded,
} from "@mui/icons-material";

const MotionBox = motion(Box);

const MATCH_RANGE_OPTIONS = [
    { id: "90", label: "Last 90 matches", maxMatches: 90 },
    { id: "180", label: "Last 180 matches", maxMatches: 180 },
    { id: "270", label: "Last 270 matches", maxMatches: 270 },
    { id: "all", label: "All Matches", maxMatches: null },
];

const PROFILE_PERFORMANCE_CARD_SX = {
    display: "flex",
    flexDirection: "column",
    borderRadius: "12px",
    position: "relative",
    background: `
        linear-gradient(
            180deg,
            rgba(12,12,14,0.96) 0%,
            rgba(8,8,10,0.985) 100%
        )
    `,
    border: "1px solid rgba(255,255,255,0.05)",
    backdropFilter: "blur(18px)",
    boxShadow: `
        inset 0 1px 0 rgba(255,255,255,0.05),
        inset 0 -40px 80px rgba(246,185,76,0.03),
        0 24px 60px rgba(0,0,0,0.42)
    `,
    gap: "1rem",
    padding: "1.5rem",
    minWidth: 0,
};

const CARD_TOP_GLOW_SX = {
    position: "absolute",
    top: 0,
    left: "50%",
    transform: "translateX(-50%)",
    width: "42%",
    height: "1px",
    background: `
        linear-gradient(
            90deg,
            transparent,
            rgba(246,185,76,0.95),
            transparent
        )
    `,
    boxShadow: "0 0 18px rgba(246,185,76,0.45)",
    pointerEvents: "none",
};

const PERFORMANCE_SURFACE_SX = {
    background: `
        linear-gradient(
            180deg,
            rgba(14,14,17,0.98) 0%,
            rgba(9,9,11,0.99) 100%
        )
    `,
    border: "1px solid rgba(255,255,255,0.05)",
    boxShadow: `
        inset 0 1px 0 rgba(255,255,255,0.04),
        inset 0 -24px 48px rgba(246,185,76,0.02)
    `,
};

const MAP_VIEW_OPTIONS = [
    { id: "all", label: "All Maps" },
    { id: "best3", label: "Best 3" },
];

function getRangeMatchCount(totalMatchCount, maxMatches) {
    if (maxMatches == null) {
        return totalMatchCount;
    }
    return Math.min(totalMatchCount, maxMatches);
}

function MatchRangeFilterButton({ label, isActive, onClick, layoutId = "activeMatchRangeFilter" }) {
    return (
        <MotionBox
            component="button"
            type="button"
            onClick={onClick}
            aria-pressed={isActive}
            whileHover={{ y: -1 }}
            whileTap={{ scale: 0.97 }}
            transition={{
                type: "spring",
                stiffness: 320,
                damping: 24,
            }}
            sx={{
                position: "relative",
                overflow: "hidden",
                border: "none",
                cursor: "pointer",
                borderRadius: "999px",
                px: { xs: "0.7rem", md: "1rem" },
                py: { xs: "0.42rem", md: "0.58rem" },
                minWidth: { xs: "auto", md: "84px" },
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: { xs: "0.6rem", md: "0.74rem" },
                fontWeight: 700,
                letterSpacing: { xs: "0.05em", md: "0.08em" },
                textTransform: "uppercase",
                color: isActive
                    ? colors.text.primary
                    : "rgba(255,255,255,0.46)",
                background: isActive
                    ? `
                        linear-gradient(
                            180deg,
                            rgba(255,255,255,0.06) 0%,
                            rgba(246,185,76,0.10) 100%
                        )
                    `
                    : "rgba(255,255,255,0.015)",
                borderWidth: "1px",
                borderStyle: "solid",
                borderColor: isActive
                    ? "rgba(246,185,76,0.24)"
                    : "rgba(255,255,255,0.06)",
                backdropFilter: "blur(10px)",
                boxShadow: isActive
                    ? `
                        0 0 0 1px rgba(246,185,76,0.04),
                        0 6px 18px rgba(0,0,0,0.24),
                        0 0 24px rgba(246,185,76,0.08)
                    `
                    : `inset 0 1px 0 rgba(255,255,255,0.02)`,
                transition: `
                    background 240ms ease,
                    color 200ms ease,
                    border-color 220ms ease,
                    box-shadow 260ms ease
                `,
                "&:hover": {
                    color: colors.text.primary,
                    borderColor: isActive
                        ? "rgba(246,185,76,0.30)"
                        : "rgba(255,255,255,0.12)",
                    background: isActive
                        ? `
                            linear-gradient(
                                180deg,
                                rgba(255,255,255,0.08) 0%,
                                rgba(246,185,76,0.14) 100%
                            )
                        `
                        : "rgba(255,255,255,0.03)",
                },
                "&::before": {
                    content: '""',
                    position: "absolute",
                    inset: 0,
                    opacity: isActive ? 1 : 0,
                    background: `
                        linear-gradient(
                            120deg,
                            transparent 20%,
                            rgba(255,255,255,0.06) 50%,
                            transparent 80%
                        )
                    `,
                    transition: "opacity 220ms ease",
                    pointerEvents: "none",
                },
            }}
        >
            <AnimatePresence mode="wait">
                {isActive ? (
                    <MotionBox
                        layoutId={layoutId}
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{
                            type: "spring",
                            stiffness: 280,
                            damping: 26,
                        }}
                        sx={{
                            position: "absolute",
                            inset: 0,
                            borderRadius: "999px",
                            border: "1px solid rgba(246,185,76,0.14)",
                            boxShadow: "0 0 22px rgba(246,185,76,0.10)",
                            pointerEvents: "none",
                        }}
                    />
                ) : null}
            </AnimatePresence>
            <Box component="span" sx={{ position: "relative", zIndex: 2 }}>
                {label}
            </Box>
        </MotionBox>
    );
}

function formatEloValue(value) {
    if (!Number.isFinite(value)) {
        return "—";
    }
    return value.toLocaleString("en-US");
}

function StatBlock({ label, value, valueColor = colors.text.primary }) {
    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "baseline",
                justifyContent: "space-between",
                gap: "0.5rem",
                minWidth: 0,
            }}
        >
            <Typography
                sx={{
                    fontSize: "0.65rem",
                    fontWeight: 700,
                    letterSpacing: "0.12em",
                    textTransform: "uppercase",
                    color: "rgba(255,255,255,0.38)",
                    lineHeight: 1.2,
                    flexShrink: 0,
                }}
            >
                {label}
            </Typography>
            <Typography
                sx={{
                    fontSize: "0.88rem",
                    fontWeight: 800,
                    color: valueColor,
                    lineHeight: 1,
                    fontVariantNumeric: "tabular-nums",
                    letterSpacing: "-0.01em",
                    textAlign: "right",
                    minWidth: 0,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                }}
            >
                {value}
            </Typography>
        </Box>
    );
}

function formatRating3Value(value) {
    if (!Number.isFinite(value)) {
        return "—";
    }
    return value.toFixed(2);
}

function PerformancePanelChrome() {
    return (
        <>
            <Box
                sx={{
                    position: "absolute",
                    inset: 0,
                    background: `
                        radial-gradient(
                            ellipse 90% 55% at 50% 0%,
                            rgba(246,185,76,0.07) 0%,
                            rgba(246,185,76,0.02) 35%,
                            transparent 72%
                        )
                    `,
                    pointerEvents: "none",
                }}
            />
        </>
    );
}

function EloProgressionStatsPanel({
    stats,
    recordWins,
    recordLosses,
    winRate,
    formattedEloChange,
    eloChangeColor,
}) {
    const totalGames = recordWins + recordLosses;
    const winRateDisplay = `${winRate}%`;

    return (
        <Box
            sx={{
                position: "relative",
                width: "100%",
                maxWidth: "100%",
                boxSizing: "border-box",
                borderRadius: "12px",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                gap: "0.95rem",
                px: "1.25rem",
                py: "1rem",
                ...PERFORMANCE_SURFACE_SX,
            }}
        >
            <PerformancePanelChrome />

            <Box sx={{ position: "relative", zIndex: 1, textAlign: "center", pt: "0.15rem" }}>
                <Typography
                    sx={{
                        fontSize: "1.75rem",
                        fontWeight: 800,
                        lineHeight: 1,
                        letterSpacing: "-0.03em",
                        fontVariantNumeric: "tabular-nums",
                        color: eloChangeColor,
                        textShadow:
                            stats.eloChange > 0
                                ? `0 0 20px ${alpha(colors.state.success.main, 0.18)}`
                                : stats.eloChange < 0
                                    ? `0 0 20px ${alpha(colors.state.danger.main, 0.14)}`
                                    : "none",
                    }}
                >
                    {formattedEloChange}
                </Typography>
                <Typography
                    sx={{
                        mt: "0.35rem",
                        fontSize: "0.54rem",
                        fontWeight: 700,
                        letterSpacing: "0.16em",
                        textTransform: "uppercase",
                        color: "rgba(255,255,255,0.36)",
                        lineHeight: 1,
                    }}
                >
                    Net Gain
                </Typography>
            </Box>

            <Box sx={{ position: "relative", zIndex: 1 }}>
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        gap: "0.75rem",
                        mb: "0.55rem",
                    }}
                >
                    <Typography
                        sx={{
                            fontSize: "0.825rem",
                            fontWeight: 700,
                            color: colors.text.primary,
                            lineHeight: 1.3,
                            fontVariantNumeric: "tabular-nums",
                        }}
                    >
                        <Box component="span" sx={{ color: colors.state.success.main }}>
                            {recordWins}W
                        </Box>
                        <Box
                            component="span"
                            sx={{ mx: "0.4rem", color: "rgba(255,255,255,0.22)" }}
                        >
                            •
                        </Box>
                        <Box component="span" sx={{ color: colors.state.danger.main }}>
                            {recordLosses}L
                        </Box>
                    </Typography>

                    <Typography
                        sx={{
                            fontSize: "0.7rem",
                            fontWeight: 600,
                            letterSpacing: "0.05em",
                            color: "rgba(255,255,255,0.42)",
                            fontVariantNumeric: "tabular-nums",
                            flexShrink: 0,
                        }}
                    >
                        {winRateDisplay}
                    </Typography>
                </Box>

                <Box
                    sx={{
                        height: "3px",
                        borderRadius: "999px",
                        backgroundColor: "rgba(255,255,255,0.06)",
                        overflow: "hidden",
                    }}
                >
                    <Box
                        sx={{
                            width: totalGames > 0 ? `${(recordWins / totalGames) * 100}%` : "0%",
                            height: "100%",
                            borderRadius: "999px",
                            background: `linear-gradient(
                                90deg,
                                ${alpha(colors.state.success.main, 0.75)} 0%,
                                ${colors.state.success.main} 100%
                            )`,
                            boxShadow: `0 0 10px ${alpha(colors.state.success.main, 0.35)}`,
                        }}
                    />
                </Box>
            </Box>

            <Box
                sx={{
                    position: "relative",
                    zIndex: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.65rem",
                }}
            >
                <StatBlock
                    label="Peak Rating"
                    value={formatEloValue(stats.highestElo)}
                />
                <StatBlock
                    label="Lowest Point"
                    value={formatEloValue(stats.lowestElo)}
                />
                <StatBlock
                    label="Net Gain"
                    value={formattedEloChange}
                    valueColor={eloChangeColor}
                />
            </Box>
        </Box>
    );
}

EloProgressionStatsPanel.propTypes = {
    stats: PropTypes.shape({
        highestElo: PropTypes.number,
        lowestElo: PropTypes.number,
        eloChange: PropTypes.number,
    }).isRequired,
    recordWins: PropTypes.number.isRequired,
    recordLosses: PropTypes.number.isRequired,
    winRate: PropTypes.number.isRequired,
    formattedEloChange: PropTypes.string.isRequired,
    eloChangeColor: PropTypes.string.isRequired,
};

function Rating3ProgressionStatsPanel({ ratingStats }) {
    const currentColor =
        Number.isFinite(ratingStats.current) && ratingStats.current < 1
            ? colors.state.danger.main
            : colors.primary.main;

    return (
        <Box
            sx={{
                position: "relative",
                width: "100%",
                maxWidth: "100%",
                boxSizing: "border-box",
                borderRadius: "12px",
                overflow: "hidden",
                display: "flex",
                flexDirection: "column",
                gap: "0.95rem",
                px: "1.25rem",
                py: "1rem",
                ...PERFORMANCE_SURFACE_SX,
            }}
        >
            <PerformancePanelChrome />

            <Box sx={{ position: "relative", zIndex: 1, textAlign: "center", pt: "0.15rem" }}>
                <Typography
                    sx={{
                        fontSize: "1.75rem",
                        fontWeight: 800,
                        lineHeight: 1,
                        letterSpacing: "-0.03em",
                        fontVariantNumeric: "tabular-nums",
                        color: currentColor,
                        textShadow: `0 0 20px ${alpha(currentColor, 0.18)}`,
                    }}
                >
                    {formatRating3Value(ratingStats.current)}
                </Typography>
                <Typography
                    sx={{
                        mt: "0.35rem",
                        fontSize: "0.54rem",
                        fontWeight: 700,
                        letterSpacing: "0.16em",
                        textTransform: "uppercase",
                        color: "rgba(255,255,255,0.36)",
                        lineHeight: 1,
                    }}
                >
                    Current
                </Typography>
            </Box>

            <Box
                sx={{
                    position: "relative",
                    zIndex: 1,
                    display: "flex",
                    flexDirection: "column",
                    gap: "0.65rem",
                }}
            >
                <StatBlock
                    label="Peak"
                    value={formatRating3Value(ratingStats.peak)}
                    valueColor={colors.primary.main}
                />
                <StatBlock
                    label="Lowest"
                    value={formatRating3Value(ratingStats.lowest)}
                />
                <StatBlock
                    label="Avg"
                    value={formatRating3Value(ratingStats.avg)}
                />
            </Box>
        </Box>
    );
}

Rating3ProgressionStatsPanel.propTypes = {
    ratingStats: PropTypes.shape({
        current: PropTypes.number,
        peak: PropTypes.number,
        lowest: PropTypes.number,
        avg: PropTypes.number,
    }).isRequired,
};

function formatCombatStat(value, digits = 0) {
    if (!Number.isFinite(value)) {
        return "—";
    }
    return value.toFixed(digits);
}

function PerformanceStatCard({ value, label }) {
    return (
        <Box
            sx={{
                position: "relative",
                minWidth: 0,
                borderRadius: "12px",
                overflow: "hidden",
                px: "1rem",
                py: "0.9rem",
                ...PERFORMANCE_SURFACE_SX,
            }}
        >
            <Typography
                sx={{
                    fontSize: "1.2rem",
                    fontWeight: 800,
                    color: colors.text.primary,
                    lineHeight: 1,
                    fontVariantNumeric: "tabular-nums",
                    letterSpacing: "-0.02em",
                }}
            >
                {value}
            </Typography>
            <Typography
                sx={{
                    mt: "0.45rem",
                    fontSize: "0.75rem",
                    fontWeight: 700,
                    letterSpacing: "0.12em",
                    textTransform: "uppercase",
                    color: "rgba(255,255,255,0.38)",
                    lineHeight: 1,
                }}
            >
                {label}
            </Typography>
        </Box>
    );
}

PerformanceStatCard.propTypes = {
    value: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
};

function PerformanceCombatStatsRow({ combatStats, isLoading }) {
    const cards = isLoading
        ? [
            { value: "—", label: "K/D/A" },
            { value: "—", label: "K/D" },
            { value: "—", label: "K/R" },
            { value: "—", label: "HS%" },
            { value: "—", label: "ADR" },
        ]
        : [
            {
                value: `${formatCombatStat(combatStats.avgKills, 0)} / ${formatCombatStat(combatStats.avgDeaths, 0)} / ${formatCombatStat(combatStats.avgAssists, 0)}`,
                label: "K/D/A",
            },
            {
                value: formatCombatStat(combatStats.kd, 2),
                label: "K/D",
            },
            {
                value: formatCombatStat(combatStats.kr, 2),
                label: "K/R",
            },
            {
                value: Number.isFinite(combatStats.hsPercent)
                    ? `${formatCombatStat(combatStats.hsPercent, 0)}%`
                    : "—",
                label: "HS%",
            },
            {
                value: formatCombatStat(combatStats.adr, 1),
                label: "ADR",
            },
        ];

    return (
        <Box
            sx={{
                display: "grid",
                gridTemplateColumns: {
                    xs: "repeat(2, minmax(0, 1fr))",
                    sm: "repeat(3, minmax(0, 1fr))",
                    lg: "repeat(5, minmax(0, 1fr))",
                },
                gap: "0.65rem",
                width: "100%",
                minWidth: 0,
                mt: "0.5rem",
            }}
        >
            {cards.map((card) => (
                <PerformanceStatCard
                    key={card.label}
                    value={card.value}
                    label={card.label}
                />
            ))}
        </Box>
    );
}

PerformanceCombatStatsRow.propTypes = {
    combatStats: PropTypes.shape({
        avgKills: PropTypes.number,
        avgDeaths: PropTypes.number,
        avgAssists: PropTypes.number,
        kd: PropTypes.number,
        kr: PropTypes.number,
        hsPercent: PropTypes.number,
        adr: PropTypes.number,
    }).isRequired,
    isLoading: PropTypes.bool,
};

export default function ProfilePerformanceTab({
    last25MatchData = {},
    matchSummary = {},
    rating3 = null,
    mapsStats = {},
    isLoading = false,
}) {
    const [selectedRangeId, setSelectedRangeId] = useState("90");
    const [mapViewId, setMapViewId] = useState("all");

    const totalMatchCount = useMemo(
        () => getProfileTotalMatchCount(last25MatchData, matchSummary),
        [last25MatchData, matchSummary],
    );

    const visibleRangeOptions = useMemo(
        () =>
            MATCH_RANGE_OPTIONS.filter((option) => {
                if (option.id === "180" || option.id === "270") {
                    return totalMatchCount >= 90;
                }
                return true;
            }),
        [totalMatchCount],
    );

    const selectedRange = useMemo(
        () =>
            visibleRangeOptions.find((option) => option.id === selectedRangeId) ||
            visibleRangeOptions[0] ||
            MATCH_RANGE_OPTIONS[0],
        [selectedRangeId, visibleRangeOptions],
    );

    useEffect(() => {
        if (!visibleRangeOptions.some((option) => option.id === selectedRangeId)) {
            setSelectedRangeId(visibleRangeOptions[0]?.id || "90");
        }
    }, [visibleRangeOptions, selectedRangeId]);

    const hasMatches = useMemo(
        () => buildProfileMatches(last25MatchData).length > 0,
        [last25MatchData],
    );

    const stats = useMemo(
        () =>
            buildEloProgressionStats(
                last25MatchData,
                selectedRange.maxMatches,
            ),
        [last25MatchData, selectedRange.maxMatches],
    );

    const useMatchSummaryRecord = useMemo(
        () => shouldUseMatchSummaryForRecord(selectedRange, matchSummary),
        [selectedRange, matchSummary],
    );

    const recordStats = useMemo(() => {
        if (useMatchSummaryRecord) {
            const wins = matchSummary.totalMatchesWon ?? 0;
            const losses = matchSummary.totalMatchesLost ?? 0;
            const total = wins + losses;
            const winRate =
                total > 0
                    ? matchSummary.winRate ?? Math.round((wins / total) * 100)
                    : 0;

            return { wins, losses, winRate };
        }

        const wins = stats.wins;
        const losses = stats.losses;
        const total = wins + losses;
        const winRate =
            total > 0
                ? Math.round((wins / total) * 1000) / 10
                : 0;

        return { wins, losses, winRate };
    }, [useMatchSummaryRecord, matchSummary, stats.wins, stats.losses]);

    const combatStats = useMemo(
        () =>
            buildPerformanceCombatStats(
                last25MatchData,
                selectedRange.maxMatches,
            ),
        [last25MatchData, selectedRange.maxMatches],
    );

    const rating3Points = useMemo(
        () =>
            buildRating3TrendSeries(last25MatchData, selectedRange.maxMatches)
                .points,
        [last25MatchData, selectedRange.maxMatches],
    );

    const rating3Stats = useMemo(
        () =>
            buildRating3ProgressionStats(
                rating3Points,
                rating3?.current,
            ),
        [rating3Points, rating3?.current],
    );

    const showRating3Section =
        isLoading || rating3Points.length >= 2 || Number.isFinite(rating3?.current);

    const eloChangeColor =
        stats.eloChange > 0
            ? colors.state.success.main
            : stats.eloChange < 0
                ? colors.state.danger.main
                : colors.text.muted;

    const formattedEloChange = Number.isFinite(stats.eloChange)
        ? `${stats.eloChange > 0 ? "+" : ""}${stats.eloChange.toLocaleString("en-US")}`
        : "—";

    if (!isLoading && !hasMatches) {
        return (
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    gap: "1rem",
                    width: "100%",
                    maxWidth: "100%",
                    alignSelf: "stretch",
                }}
            >
                <EmptyDataState
                    sectionTitle="Elo Progression"
                    sectionSubtitle="Competitive rating over time"
                    title="No stats available"
                    icon={ShowChartRounded}
                    minHeight="16rem"
                >
                    Matches needed to unlock performance stats
                </EmptyDataState>
                <Box
                    sx={{
                        display: { xs: "flex", lg: "grid" },
                        gridTemplateColumns: {
                            lg: "minmax(0, 1.2fr) minmax(0, 1fr)",
                        },
                        flexDirection: { xs: "column", lg: "unset" },
                        gap: "1rem",
                        width: "100%",
                        alignItems: "stretch",
                    }}
                >
                    <EmptyDataState
                        sectionTitle="Map Performance"
                        sectionSubtitle="Win rate and form by map"
                        title="No map data"
                        icon={MapRounded}
                        minHeight="18rem"
                    >
                        Matches needed to unlock map performance
                    </EmptyDataState>
                    <Box
                        sx={{
                            display: "flex",
                            flexDirection: "column",
                            gap: "1rem",
                            minWidth: 0,
                        }}
                    >
                        <EmptyDataState
                            sectionTitle="Combat Trends"
                            sectionSubtitle="ADR · KAST% · HS%"
                            title="No combat stats"
                            icon={TimelineRounded}
                            minHeight="12rem"
                        >
                            Matches needed to unlock combat trends
                        </EmptyDataState>
                        <EmptyDataState
                            sectionTitle="Map Play Profile"
                            sectionSubtitle="Strengths across your map pool"
                            title="No profile yet"
                            icon={HubRounded}
                            minHeight="12rem"
                        >
                            Matches needed to unlock map play profile
                        </EmptyDataState>
                    </Box>
                </Box>
            </Box>
        );
    }

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                gap: "1rem",
                width: "100%",
                maxWidth: "100%",
                alignSelf: "stretch",
            }}
        >
            <Box
                sx={{
                    ...PROFILE_PERFORMANCE_CARD_SX,
                    width: "100%",
                    maxWidth: "100%",
                    overflow: "hidden",
                    boxSizing: "border-box",
                }}
            >
            <Box sx={CARD_TOP_GLOW_SX} />
            <Typography
                sx={{
                    position: "relative",
                    zIndex: 2,
                    fontSize: "0.72rem",
                    fontWeight: 800,
                    letterSpacing: "0.16em",
                    textTransform: "uppercase",
                    color: colors.text.primary,
                    lineHeight: 1,
                }}
            >
                Elo Progression
            </Typography>

            <Box
                sx={{
                    position: "relative",
                    zIndex: 2,
                    display: "flex",
                    flexWrap: "wrap",
                    alignItems: "center",
                    gap: "0.5rem",
                }}
            >
                {visibleRangeOptions.map((option) => {
                    const rangeCount = getRangeMatchCount(
                        totalMatchCount,
                        option.maxMatches,
                    );

                    return (
                        <MatchRangeFilterButton
                            key={option.id}
                            label={`${option.label} (${rangeCount})`}
                            isActive={selectedRangeId === option.id}
                            onClick={() => setSelectedRangeId(option.id)}
                        />
                    );
                })}
            </Box>

            <Box
                sx={{
                    position: "relative",
                    zIndex: 2,
                    display: "flex",
                    flexDirection: "column",
                    gap: "1rem",
                    width: "100%",
                    maxWidth: "100%",
                    minWidth: 0,
                    mt: "1rem",
                    overflow: "hidden",
                }}
            >
                <Box
                    sx={{
                        display: { xs: "flex", md: "grid" },
                        gridTemplateColumns: {
                            md: "minmax(0, 1fr) minmax(0, 20rem)",
                        },
                        flexDirection: { xs: "column", md: "unset" },
                        gap: { xs: "1rem", md: "1.25rem" },
                        alignItems: { md: "start" },
                        width: "100%",
                        minWidth: 0,
                    }}
                >
                    <Box sx={{ minWidth: 0, maxWidth: "100%", overflow: "hidden" }}>
                        <OverviewEloProgressionChart
                            last25MatchData={last25MatchData}
                            isLoading={isLoading}
                            maxMatches={selectedRange.maxMatches}
                        />
                    </Box>

                    <Box
                        sx={{
                            width: "100%",
                            minWidth: { md: "18rem" },
                            maxWidth: { md: "20rem" },
                            justifySelf: { md: "end" },
                        }}
                    >
                        <EloProgressionStatsPanel
                            stats={stats}
                            recordWins={recordStats.wins}
                            recordLosses={recordStats.losses}
                            winRate={recordStats.winRate}
                            formattedEloChange={formattedEloChange}
                            eloChangeColor={eloChangeColor}
                        />
                    </Box>
                </Box>

                <PerformanceCombatStatsRow
                    combatStats={combatStats}
                    isLoading={isLoading}
                />
            </Box>
            </Box>

            {showRating3Section ? (
                <Box
                    sx={{
                        ...PROFILE_PERFORMANCE_CARD_SX,
                        width: "100%",
                        maxWidth: "100%",
                        overflow: "hidden",
                        boxSizing: "border-box",
                    }}
                >
                    <Box sx={CARD_TOP_GLOW_SX} />
                    <Typography
                        sx={{
                            position: "relative",
                            zIndex: 2,
                            fontSize: "0.72rem",
                            fontWeight: 800,
                            letterSpacing: "0.16em",
                            textTransform: "uppercase",
                            color: colors.text.primary,
                            lineHeight: 1,
                        }}
                    >
                        Rating 3.0
                    </Typography>

                    <Box
                        sx={{
                            position: "relative",
                            zIndex: 2,
                            display: { xs: "flex", md: "grid" },
                            gridTemplateColumns: {
                                md: "minmax(0, 1fr) minmax(0, 20rem)",
                            },
                            flexDirection: { xs: "column", md: "unset" },
                            gap: { xs: "1rem", md: "1.25rem" },
                            alignItems: { md: "start" },
                            width: "100%",
                            minWidth: 0,
                            mt: "1rem",
                        }}
                    >
                        <Box sx={{ minWidth: 0, maxWidth: "100%", overflow: "hidden" }}>
                            <OverviewRatingTrendChart
                                last25MatchData={last25MatchData}
                                isLoading={isLoading}
                                maxMatches={selectedRange.maxMatches}
                                showFooterStats={false}
                            />
                        </Box>

                        <Box
                            sx={{
                                width: "100%",
                                minWidth: { md: "18rem" },
                                maxWidth: { md: "20rem" },
                                justifySelf: { md: "end" },
                            }}
                        >
                            <Rating3ProgressionStatsPanel ratingStats={rating3Stats} />
                        </Box>
                    </Box>
                </Box>
            ) : null}

            <Box
                sx={{
                    display: { xs: "flex", lg: "grid" },
                    gridTemplateColumns: { lg: "minmax(0, 1.2fr) minmax(0, 1fr)" },
                    flexDirection: { xs: "column", lg: "unset" },
                    gap: "1rem",
                    width: "100%",
                    maxWidth: "100%",
                    alignItems: "stretch",
                }}
            >
                <Box
                    sx={{
                        ...PROFILE_PERFORMANCE_CARD_SX,
                        width: "100%",
                        maxWidth: "100%",
                        overflow: "hidden",
                        boxSizing: "border-box",
                    }}
                >
                    <Box sx={CARD_TOP_GLOW_SX} />
                    <Box sx={{ position: "relative", zIndex: 2 }}>
                        <Typography
                            sx={{
                                fontSize: "0.72rem",
                                fontWeight: 800,
                                letterSpacing: "0.16em",
                                textTransform: "uppercase",
                                color: colors.text.primary,
                                lineHeight: 1,
                            }}
                        >
                            Map Performance
                        </Typography>
                    </Box>
                    <Box
                        sx={{
                            position: "relative",
                            zIndex: 2,
                            display: "flex",
                            flexWrap: "wrap",
                            alignItems: "center",
                            gap: "0.5rem",
                            mt: "0.75rem",
                        }}
                    >
                        {MAP_VIEW_OPTIONS.map((option) => (
                            <MatchRangeFilterButton
                                key={option.id}
                                label={option.label}
                                isActive={mapViewId === option.id}
                                onClick={() => setMapViewId(option.id)}
                                layoutId="activeMapPerformanceFilter"
                            />
                        ))}
                    </Box>
                    <Box sx={{ position: "relative", zIndex: 2, minWidth: 0, mt: "0.75rem" }}>
                        <MapPerformanceList
                            mapsStats={mapsStats}
                            last25MatchData={last25MatchData}
                            isLoading={isLoading}
                            limit={mapViewId === "best3" ? 3 : null}
                        />
                    </Box>
                </Box>

                <Box
                    sx={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "1rem",
                        minWidth: 0,
                        minHeight: 0,
                    }}
                >
                    <Box
                        sx={{
                            ...PROFILE_PERFORMANCE_CARD_SX,
                            width: "100%",
                            maxWidth: "100%",
                            overflow: "hidden",
                            boxSizing: "border-box",
                        }}
                    >
                        <Box sx={CARD_TOP_GLOW_SX} />
                        <Box sx={{ position: "relative", zIndex: 2 }}>
                            <Typography
                                sx={{
                                    fontSize: "0.72rem",
                                    fontWeight: 800,
                                    letterSpacing: "0.16em",
                                    textTransform: "uppercase",
                                    color: colors.text.primary,
                                    lineHeight: 1,
                                }}
                            >
                                Combat Trends
                            </Typography>
                            <Typography
                                sx={{
                                    mt: "0.35rem",
                                    fontSize: "0.68rem",
                                    fontWeight: 500,
                                    letterSpacing: "0.04em",
                                    color: "rgba(255,255,255,0.42)",
                                    lineHeight: 1.35,
                                }}
                            >
                                {selectedRange.label}
                                {mapViewId === "best3" ? " · top 3 maps" : ""}
                            </Typography>
                        </Box>
                        <Box sx={{ position: "relative", zIndex: 2, minWidth: 0, mt: "0.75rem" }}>
                            <MapCombatTrendChart
                                mapsStats={mapsStats}
                                last25MatchData={last25MatchData}
                                isLoading={isLoading}
                                limit={mapViewId === "best3" ? 3 : null}
                                maxMatches={selectedRange.maxMatches}
                            />
                        </Box>
                    </Box>

                    <Box
                        sx={{
                            ...PROFILE_PERFORMANCE_CARD_SX,
                            width: "100%",
                            maxWidth: "100%",
                            overflow: "hidden",
                            boxSizing: "border-box",
                            display: "flex",
                            flexDirection: "column",
                        }}
                    >
                        <Box sx={CARD_TOP_GLOW_SX} />
                        <Box sx={{ position: "relative", zIndex: 2, flexShrink: 0 }}>
                            <Typography
                                sx={{
                                    fontSize: "0.72rem",
                                    fontWeight: 800,
                                    letterSpacing: "0.16em",
                                    textTransform: "uppercase",
                                    color: colors.text.primary,
                                    lineHeight: 1,
                                }}
                            >
                                Map Play Profile
                            </Typography>
                            <Typography
                                sx={{
                                    mt: "0.35rem",
                                    fontSize: "0.68rem",
                                    fontWeight: 500,
                                    letterSpacing: "0.04em",
                                    color: "rgba(255,255,255,0.42)",
                                    lineHeight: 1.35,
                                }}
                            >
                                {mapViewId === "best3"
                                    ? "Strengths across your top 3 maps"
                                    : "Strengths across your full map pool"}
                            </Typography>
                        </Box>
                        <Box
                            sx={{
                                position: "relative",
                                zIndex: 2,
                                mt: "0.75rem",
                                display: "flex",
                                flexDirection: "column",
                                minWidth: 0,
                                width: "100%",
                            }}
                        >
                            <MapPlayRadarChart
                                mapsStats={mapsStats}
                                last25MatchData={last25MatchData}
                                isLoading={isLoading}
                                limit={mapViewId === "best3" ? 3 : null}
                            />
                        </Box>
                    </Box>
                </Box>
            </Box>
        </Box>
    );
}
